// client/src/pages/HomePage.js
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useApi, api } from '../hooks/useApi';
import UserAvatar from '../components/UserAvatar';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor,
  useSensor, useSensors, DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable,
  verticalListSortingStrategy, rectSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function calcAge(birthDate) {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() < birth.getMonth() ||
      (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
  return age;
}

// ─── Mobil hjemmeside ─────────────────────────────────────────
const DEFAULT_WIDGETS = ['members','stats','todos','messages'];
function getMobileWidgets() {
  try { return JSON.parse(localStorage.getItem('mobileWidgets') || JSON.stringify(DEFAULT_WIDGETS)); }
  catch { return DEFAULT_WIDGETS; }
}

const WIDGET_LABELS = {
  members:  'Familiemedlemmer',
  stats:    'Hurtigstatistikk',
  todos:    'Huskeliste',
  messages: 'Siste melding',
  upcoming: 'Kommende hendelser',
  weather:  'Vær',
  homework: 'Lekser',
  budget:   'Budsjett-oversikt',
  shopping: 'Handleliste',
  meals:    'Matplan',
};

function MobileHome() {
  const { user, family, members } = useAuth();
  const { dark } = useTheme();
  const navigate  = useNavigate();
  const today     = new Date().toISOString().slice(0,10);
  const in7days   = new Date(Date.now()+7*86400000).toISOString();
  const thisMonth = today.slice(0,7);

  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const [visibleWidgets, setVisibleWidgets] = useState(getMobileWidgets);

  const toggleWidget = (id) => {
    setVisibleWidgets(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      localStorage.setItem('mobileWidgets', JSON.stringify(next));
      return next;
    });
  };

  const in30days = new Date(Date.now()+30*86400000).toISOString();

  const { data: todos }       = useApi('/api/todos');
  const { data: events }      = useApi(`/api/events?from=${today}T00:00:00Z&to=${in7days}`);
  const { data: allEvents }   = useApi(`/api/events?from=${today}T00:00:00Z&to=${in30days}`);
  const { data: messages }    = useApi('/api/messages?limit=3');
  const { data: shoppingData }= useApi('/api/shopping');
  const { data: budget }      = useApi(`/api/budget?month=${thisMonth}`);
  const { data: income }      = useApi(`/api/income?month=${thisMonth}`);
  const { data: weather }     = useApi('/api/weather');
  const { data: homeworkData }= useApi('/api/homework');
  const { data: mealData }    = useApi(`/api/meals?week=${today.slice(0,7)}`);

  const hour = new Date().getHours();
  const greeting = hour < 5 ? 'God natt' : hour < 12 ? 'God morgen' : hour < 17 ? 'God ettermiddag' : 'God kveld';

  const txt    = dark ? '#E8E8F8' : '#1A1A35';
  const sub    = dark ? '#9090B8' : '#888888';
  const border = dark ? 'rgba(255,255,255,.07)' : '#F5F5F5';

  const todayEvCount  = (events||[]).filter(e => e.start_time?.startsWith(today)).length;
  const shoppingItems = shoppingData?.items || [];
  const shoppingCount = shoppingItems.filter(s => !s.checked).length;
  const totalExp   = (budget?.expenses||[]).reduce((s,e)=>s+e.amount, 0);
  const totalInc   = (income||[]).reduce((s,i)=>s+i.amount, 0);
  const saldo      = totalInc - totalExp;
  const openTodos  = (todos||[]).filter(t => !t.done);
  const lastMsg    = (messages||[])[0];

  const fmtSaldo = (n) => {
    const abs = Math.abs(n);
    const str = abs >= 1000 ? `${Math.round(abs/1000)}k` : `${Math.round(abs)}`;
    return (n >= 0 ? '+' : '-') + str;
  };

  const STATS = [
    { label:'I dag', value: todayEvCount,
      grad:'linear-gradient(135deg, #E5534B, #F7605C)',
      glow:'rgba(229,83,75,0.35)',
      icon:<svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
      onClick: ()=>navigate('/kalender') },
    { label:'Handle', value: shoppingCount,
      grad:'linear-gradient(135deg, #7C6FF7, #9B8FFF)',
      glow:'rgba(124,111,247,0.35)',
      icon:<svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 001.98 1.61h9.72a2 2 0 001.98-1.61L23 6H6"/></svg>,
      onClick: ()=>navigate('/handleliste') },
    { label:'Saldo', value: fmtSaldo(saldo),
      grad:'linear-gradient(135deg, #11B57A, #34D399)',
      glow:'rgba(17,181,122,0.35)',
      icon:<svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23,6 13.5,15.5 8.5,10.5 1,18"/><polyline points="17,6 23,6 23,12"/></svg>,
      onClick: ()=>navigate('/budsjett') },
  ];

  const heroGrad = dark
    ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
    : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)';

  return (
    <div style={{ background: dark ? '#0A0818' : '#EDE8FF', minHeight:'100%', fontFamily:"'Plus Jakarta Sans',sans-serif", paddingBottom:16 }}>

      {/* ── Gradient Hero ─────────────────────────────────────── */}
      <div style={{ background: heroGrad, padding:'20px 20px 60px', position:'relative', overflow:'hidden' }}>
        {/* Dekorativt lys-glob øverst høyre */}
        <div style={{ position:'absolute', top:-60, right:-60, width:220, height:220, borderRadius:'50%', background:'rgba(255,255,255,0.07)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-80, left:-30, width:200, height:200, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', position:'relative', zIndex:1 }}>
          <div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:3 }}>{greeting}</div>
            <div style={{ fontSize:26, fontWeight:800, color:'#fff', lineHeight:1.2 }}>
              {user?.name?.split(' ')[0] || family?.name || 'Famora'} 👋
            </div>
          </div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <button onClick={() => setShowWidgetSettings(v => !v)}
              style={{ width:38, height:38, borderRadius:'50%', background:'rgba(255,255,255,0.18)', border:'none', cursor:'pointer', fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(8px)' }}>
              ⚙️
            </button>
            <div style={{ width:44, height:44, borderRadius:'50%', background: user?.color ? user.color + 'CC' : 'rgba(255,255,255,0.25)', border:'2px solid rgba(255,255,255,0.5)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:16, color:'#fff', cursor:'pointer', flexShrink:0 }}
              onClick={() => navigate('/innstillinger')}>
              {user?.avatar || (user?.name||'?')[0].toUpperCase()}
            </div>
          </div>
        </div>

        {/* Familie-avatarer inne i hero */}
        {visibleWidgets.includes('members') && (
          <div style={{ display:'flex', gap:14, overflowX:'auto', scrollbarWidth:'none', marginTop:20, position:'relative', zIndex:1 }}>
            {(members||[]).map(m => (
              <div key={m.id} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:5, flexShrink:0, cursor:'pointer' }}>
                <UserAvatar photo={m.photo} emoji={m.avatar} name={m.name} color={m.color} size={48}
                  style={{ border:'2.5px solid rgba(255,255,255,0.5)', boxShadow:'0 2px 12px rgba(0,0,0,0.2)' }}/>
                <span style={{ fontSize:10, fontWeight:600, color:'rgba(255,255,255,0.8)', whiteSpace:'nowrap' }}>{m.name.split(' ')[0]}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Stats-glass-kort (overlapper hero med neg. margin) ─── */}
      {visibleWidgets.includes('stats') && (
        <div style={{ margin:'-40px 14px 16px', position:'relative', zIndex:2 }}>
          <div className="glass-card" style={{ padding:'14px 12px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
              {STATS.map(st => (
                <div key={st.label}
                  style={{ background: st.grad, borderRadius:14, padding:'12px 10px', cursor:'pointer', display:'flex', flexDirection:'column', gap:5, boxShadow:`0 4px 20px ${st.glow}`, transition:'transform .12s, box-shadow .12s' }}
                  onClick={st.onClick}
                  onTouchStart={e => { e.currentTarget.style.transform='scale(.96)'; e.currentTarget.style.boxShadow='none'; }}
                  onTouchEnd={e => { e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.boxShadow=`0 4px 20px ${st.glow}`; }}>
                  {st.icon}
                  <div style={{ fontSize:24, fontWeight:800, color:'#fff', lineHeight:1 }}>{st.value}</div>
                  <div style={{ fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.82)' }}>{st.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Widget-innstillinger ─────────────────────────────── */}
      {showWidgetSettings && (
        <div className="glass-card" style={{ margin:'0 14px 16px', padding:'14px 16px' }}>
          <div style={{ fontSize:13, fontWeight:800, color:txt, marginBottom:12 }}>Velg widgets</div>
          {Object.entries(WIDGET_LABELS).map(([id, label]) => {
            const on = visibleWidgets.includes(id);
            return (
              <div key={id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${border}` }}>
                <span style={{ fontSize:14, color:txt }}>{label}</span>
                <button onClick={() => toggleWidget(id)}
                  style={{ width:44, height:24, borderRadius:12, border:'none', cursor:'pointer', background: on?'#7C6FF7':'rgba(0,0,0,0.12)', position:'relative', flexShrink:0 }}>
                  <div style={{ position:'absolute', top:2, left: on?22:2, width:20, height:20, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,.2)', transition:'left .2s' }}/>
                </button>
              </div>
            );
          })}
          <button onClick={() => setShowWidgetSettings(false)}
            style={{ marginTop:12, width:'100%', padding:'9px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', fontWeight:700, cursor:'pointer', fontFamily:'inherit', fontSize:14 }}>
            Ferdig
          </button>
        </div>
      )}

      {/* Huskeliste */}
      {visibleWidgets.includes('todos') && openTodos.length > 0 && (
        <div className="glass-card" style={{ margin:'0 14px 12px', overflow:'hidden' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px 10px' }}>
            <span style={{ fontSize:15, fontWeight:800, color:txt }}>✅ Huskeliste</span>
            <button style={{ background:'none', border:'none', color:'#7C6FF7', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }} onClick={() => navigate('/todo')}>Se alle</button>
          </div>
          {openTodos.slice(0,4).map((t, i) => (
            <div key={t.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 16px', borderTop: i>0 ? `1px solid ${border}` : 'none' }}>
              <div style={{ width:8, height:8, borderRadius:'50%', flexShrink:0, background: t.priority==='high'?'#E5534B':t.priority==='low'?'#aaa':'#7C6FF7' }}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:600, color:txt, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.title}</div>
                {t.assigned_name && <div style={{ fontSize:11, color:sub, marginTop:1 }}>{t.assigned_name}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Siste melding */}
      {visibleWidgets.includes('messages') && lastMsg && (
        <div className="glass-card" style={{ margin:'0 14px 12px', overflow:'hidden' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px 10px' }}>
            <span style={{ fontSize:15, fontWeight:800, color:txt }}>💬 Meldinger</span>
            <button style={{ background:'none', border:'none', color:'#7C6FF7', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }} onClick={() => navigate('/chat')}>Åpne chat</button>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 16px 14px', cursor:'pointer' }} onClick={() => navigate('/chat')}>
            <UserAvatar photo={lastMsg.user_photo} emoji={lastMsg.avatar} name={lastMsg.user_name} color={lastMsg.color} size={40}/>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:14, fontWeight:700, color:txt, marginBottom:2 }}>{lastMsg.user_name}</div>
              <div style={{ fontSize:13, color:sub, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{lastMsg.content}</div>
            </div>
          </div>
        </div>
      )}

      {/* Kommende hendelser */}
      {visibleWidgets.includes('upcoming') && (allEvents||[]).length > 0 && (
        <div className="glass-card" style={{ margin:'0 14px 12px', overflow:'hidden' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px 10px' }}>
            <span style={{ fontSize:15, fontWeight:800, color:txt }}>📅 Kommende</span>
            <button style={{ background:'none', border:'none', color:'#7C6FF7', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }} onClick={() => navigate('/kalender')}>Kalender</button>
          </div>
          {(allEvents||[]).slice(0,4).map((ev, i) => (
            <div key={ev.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 16px', borderTop: i>0?`1px solid ${border}`:'none' }}>
              <div style={{ width:4, alignSelf:'stretch', borderRadius:4, background:ev.color||'#7C6FF7', flexShrink:0 }}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:600, color:txt, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ev.title}</div>
                <div style={{ fontSize:12, color:sub, marginTop:1 }}>
                  {new Date(ev.start_time).toLocaleDateString('nb-NO',{weekday:'short',day:'numeric',month:'short'})}
                  {!ev.all_day && ` kl. ${new Date(ev.start_time).toLocaleTimeString('nb-NO',{hour:'2-digit',minute:'2-digit'})}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Vær */}
      {visibleWidgets.includes('weather') && weather?.now && (
        <div className="glass-card" style={{ margin:'0 14px 12px', padding:'16px', cursor:'pointer' }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ fontSize:44, lineHeight:1 }}>
              {['clearsky','fair'].some(k=>weather.now.symbol?.includes(k))?'☀️':
               weather.now.symbol?.includes('cloud')?'☁️':
               weather.now.symbol?.includes('rain')?'🌧️':
               weather.now.symbol?.includes('snow')?'❄️':'🌡️'}
            </div>
            <div>
              <div style={{ fontSize:32, fontWeight:800, color:txt, lineHeight:1 }}>{Math.round(weather.now.temp)}°</div>
              {weather.location && <div style={{ fontSize:13, color:sub, marginTop:2 }}>📍 {weather.location}</div>}
            </div>
            {weather.forecast?.[0] && (
              <div style={{ marginLeft:'auto', textAlign:'right' }}>
                <div style={{ fontSize:12, color:sub }}>I dag</div>
                <div style={{ fontSize:14, fontWeight:700, color:'#E5534B' }}>↑{Math.round(weather.forecast[0].tempMax)}°</div>
                <div style={{ fontSize:12, color:'#60A5FA' }}>↓{Math.round(weather.forecast[0].tempMin)}°</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lekser */}
      {visibleWidgets.includes('homework') && (homeworkData||[]).filter(h=>!h.done).length > 0 && (
        <div className="glass-card" style={{ margin:'0 14px 12px', overflow:'hidden' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px 10px' }}>
            <span style={{ fontSize:15, fontWeight:800, color:txt }}>📚 Lekser</span>
            <button style={{ background:'none', border:'none', color:'#7C6FF7', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }} onClick={() => navigate('/lekser')}>Se alle</button>
          </div>
          {(homeworkData||[]).filter(h=>!h.done).slice(0,3).map((h,i) => (
            <div key={h.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 16px', borderTop: i>0?`1px solid ${border}`:'none' }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:600, color:txt, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{h.title}</div>
                <div style={{ fontSize:12, color:sub, marginTop:1 }}>{h.subject}{h.due_date ? ` · Frist ${new Date(h.due_date).toLocaleDateString('nb-NO',{day:'numeric',month:'short'})}` : ''}</div>
              </div>
              {h.user_name && <div style={{ fontSize:12, color:sub, flexShrink:0 }}>{h.user_name.split(' ')[0]}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Budsjett */}
      {visibleWidgets.includes('budget') && (
        <div className="glass-card" style={{ margin:'0 14px 12px', padding:'16px', cursor:'pointer' }}
          onClick={() => navigate('/budsjett')}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <span style={{ fontSize:15, fontWeight:800, color:txt }}>💰 Budsjett</span>
            <span style={{ fontSize:12, color:sub }}>{new Date().toLocaleDateString('nb-NO',{month:'long',year:'numeric'})}</span>
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <div style={{ flex:1, background:'linear-gradient(135deg,rgba(52,211,153,.15),rgba(52,211,153,.08))', border:'1px solid rgba(52,211,153,.25)', borderRadius:12, padding:'10px 12px' }}>
              <div style={{ fontSize:11, color:sub, marginBottom:2 }}>Inntekter</div>
              <div style={{ fontSize:18, fontWeight:800, color:'#34D399' }}>{(income||[]).reduce((s,i)=>s+i.amount,0).toLocaleString('nb-NO')} kr</div>
            </div>
            <div style={{ flex:1, background:'linear-gradient(135deg,rgba(229,83,75,.15),rgba(229,83,75,.08))', border:'1px solid rgba(229,83,75,.25)', borderRadius:12, padding:'10px 12px' }}>
              <div style={{ fontSize:11, color:sub, marginBottom:2 }}>Utgifter</div>
              <div style={{ fontSize:18, fontWeight:800, color:'#E5534B' }}>{(budget?.expenses||[]).reduce((s,e)=>s+e.amount,0).toLocaleString('nb-NO')} kr</div>
            </div>
          </div>
        </div>
      )}

      {/* Handleliste */}
      {visibleWidgets.includes('shopping') && shoppingData?.items?.length > 0 && (
        <div className="glass-card" style={{ margin:'0 14px 12px', overflow:'hidden' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px 10px' }}>
            <span style={{ fontSize:15, fontWeight:800, color:txt }}>🛒 Handleliste</span>
            <button style={{ background:'none', border:'none', color:'#7C6FF7', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }} onClick={() => navigate('/handleliste')}>Åpne</button>
          </div>
          <div style={{ padding:'4px 16px 14px', display:'flex', flexWrap:'wrap', gap:6 }}>
            {(shoppingData.items||[]).filter(i=>!i.checked).slice(0,8).map(item => (
              <span key={item.id} style={{ background: dark?'rgba(124,111,247,.18)':'rgba(124,111,247,.10)', border:'1px solid rgba(124,111,247,.25)', borderRadius:20, padding:'4px 12px', fontSize:13, color: dark?'#B8AEFF':'#5B4FD8' }}>
                {item.name}
              </span>
            ))}
            {(shoppingData.items||[]).filter(i=>!i.checked).length > 8 && (
              <span style={{ fontSize:13, color:sub }}>+{(shoppingData.items||[]).filter(i=>!i.checked).length - 8} til</span>
            )}
          </div>
        </div>
      )}

      {/* Matplan */}
      {visibleWidgets.includes('meals') && (mealData||[]).filter(m => m.date >= today).length > 0 && (
        <div className="glass-card" style={{ margin:'0 14px 12px', overflow:'hidden' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px 10px' }}>
            <span style={{ fontSize:15, fontWeight:800, color:txt }}>🍽 Matplan</span>
            <button style={{ background:'none', border:'none', color:'#7C6FF7', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }} onClick={() => navigate('/mat')}>Åpne</button>
          </div>
          {(mealData||[]).filter(m => m.date >= today).slice(0,3).map((meal, i) => (
            <div key={meal.id||i} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 16px', borderTop: i>0?`1px solid ${border}`:'none' }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:600, color:txt }}>{meal.name}</div>
                <div style={{ fontSize:12, color:sub, marginTop:1 }}>{meal.date} · {meal.meal_type}</div>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

function detectMobile() {
  const ua = navigator.userAgent.toLowerCase();
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isMobileUA   = ua.includes('android') || ua.includes('iphone') ||
                       ua.includes('ipad')    || ua.includes('mobile');
  return !(!isStandalone && !isMobileUA);
}

function useIsMobile() {
  const [m, setM] = useState(detectMobile);
  useEffect(() => {
    const h = () => setM(detectMobile());
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

// ─── Widget-definisjon ────────────────────────────────────────
const ALL_WIDGETS = [
  { id:'quicknav', label:'Hurtignavigasjon',  icon:'⚡', defaultSize:'full' },
  { id:'stats',    label:'Statistikk',        icon:'📊', defaultSize:'full' },
  { id:'weather',  label:'Vær',               icon:'🌤', defaultSize:'half' },
  { id:'news',     label:'Nyheter',           icon:'📰', defaultSize:'half' },
  { id:'upcoming', label:'Kommende hendelser',icon:'📅', defaultSize:'half' },
  { id:'todos',    label:'Oppgaver',          icon:'✅', defaultSize:'half' },
  { id:'messages', label:'Chat',              icon:'💬', defaultSize:'half' },
  { id:'members',  label:'Familien',          icon:'👨‍👩‍👧', defaultSize:'half' },
  { id:'homework', label:'Lekser',            icon:'📚', defaultSize:'half' },
];

const DEFAULT_LAYOUT = ALL_WIDGETS.map(w => ({ id: w.id, visible: true, size: w.defaultSize }));

// ─── Rad-pairing: Half+Half naboer → side om side ─────────────
// Alle andre widgets → full bredde
function buildRows(items) {
  const rows = [];
  let i = 0;
  while (i < items.length) {
    const curr = items[i];
    const next = items[i + 1];
    if ((curr.size||'full')==='half' && next && (next.size||'full')==='half') {
      rows.push([curr, next]);
      i += 2;
    } else {
      rows.push([curr]);
      i += 1;
    }
  }
  return rows;
}

// ─── Vær-helpers ─────────────────────────────────────────────
const WX = { clearsky:'☀️',fair:'🌤',partlycloudy:'⛅',cloudy:'☁️',fog:'🌫️',
  lightrain:'🌦',rain:'🌧️',heavyrain:'🌧️',rainshowers:'🌦',sleet:'🌨️',
  snow:'❄️',snowshowers:'🌨️',thunder:'⛈️',lightrainandthunder:'⛈️' };
function wxEmoji(sym){ if(!sym)return'🌡️'; const b=sym.replace(/_day|_night|_polartwilight/g,'').replace(/_/g,''); for(const[k,v]of Object.entries(WX))if(b.startsWith(k))return v; return'🌡️'; }
function fmt(t){ return t==null?'–':`${Math.round(t)}°`; }
function dayLbl(ds){ const d=new Date(ds+'T12:00:00'),diff=Math.round((d-new Date())/86400000); if(diff<=0)return'I dag'; if(diff===1)return'I morgen'; return d.toLocaleDateString('nb-NO',{weekday:'short'}); }
function newsAgo(ds){ if(!ds)return''; const d=new Date(ds); if(isNaN(d))return''; const m=Math.floor((Date.now()-d)/60000); if(m<2)return'nå'; if(m<60)return`${m}m`; const h=Math.floor(m/60); if(h<24)return`${h}t`; return`${Math.floor(h/24)}d`; }

const QUICK_NAV = [
  {path:'/kalender',   icon:'📅',label:'Kalender', color:'#7C6FF7'},
  {path:'/chat',       icon:'💬',label:'Chat',     color:'#34D399'},
  {path:'/handleliste',icon:'🛒',label:'Handle',   color:'#FBBF24'},
  {path:'/todo',       icon:'✅',label:'Todo',     color:'#F7605C'},
  {path:'/epost',      icon:'📧',label:'E-post',   color:'#60A5FA'},
];
function timeStr(iso){ if(!iso)return''; const d=new Date(iso),today=new Date().toISOString().slice(0,10); return iso.startsWith(today)?d.toLocaleTimeString('nb-NO',{hour:'2-digit',minute:'2-digit'}):d.toLocaleDateString('nb-NO',{weekday:'short',day:'numeric',month:'short'}); }
function urlB64(b){ const p='='.repeat((4-b.length%4)%4),raw=window.atob((b+p).replace(/-/g,'+').replace(/_/g,'/')); return Uint8Array.from([...raw].map(c=>c.charCodeAt(0))); }

// ─── Sortable widget (editMode: 1-kolonne liste) ───────────────
function SortableWidget({ id, editMode, visible, size, onToggle, onSize, children, label, icon }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: !editMode });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 };

  return (
    <div ref={setNodeRef} style={style}>
      {editMode && (
        <div style={sw.editBar}>
          <span {...attributes} {...listeners} style={sw.handle}>⠿</span>
          <span style={sw.editLabel}>{icon} {label}</span>
          <div style={sw.editBtns}>
            <button style={{...sw.sizeBtn,...(size==='full'?sw.sizeBtnActive:{})}} onClick={()=>onSize(id,'full')}>◼ Full</button>
            <button style={{...sw.sizeBtn,...(size==='half'?sw.sizeBtnActive:{})}} onClick={()=>onSize(id,'half')}>◧ Halv</button>
            <button style={{...sw.toggleBtn,...(visible?sw.toggleOn:sw.toggleOff)}} onClick={()=>onToggle(id)}>
              {visible ? '● Synlig' : '○ Skjult'}
            </button>
          </div>
        </div>
      )}
      {(visible || editMode) && (
        <div style={{...sw.widgetWrap,...(editMode&&!visible?sw.hiddenWidget:{})}}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Draggbar widget-wrapper (normalvisning) ──────────────────
// SizeBar = drag-håndtak. Knapper i sizeBtns stopper event-propagation
// slik at klikk ikke starter drag.
function GridWidget({ id, flex, meta, size, isMobile, onResize, onMove, onHide, totalVisible, visibleIndex, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{ flex, minWidth:0, transform:CSS.Transform.toString(transform), transition, opacity:isDragging?0.45:1 }}
      {...attributes}
    >
      <div style={s.ctrlBar} {...listeners}>
        <span style={s.ctrlGrip}>⠿</span>
        <span style={s.ctrlLabel}>{meta?.icon} {meta?.label}</span>
        <div style={s.ctrlRight} onPointerDown={e => e.stopPropagation()}>
          {!isMobile && (
            <>
              <button style={{...s.ctrlBtn,...(size==='full'?s.ctrlBtnOn:{})}} onClick={()=>onResize(id,'full')} title="Full bredde">◼</button>
              <button style={{...s.ctrlBtn,...(size==='half'?s.ctrlBtnOn:{})}} onClick={()=>onResize(id,'half')} title="Halv bredde">◧</button>
              <span style={s.ctrlDivider} />
            </>
          )}
          <button style={s.ctrlBtn} onClick={()=>onMove(id,'up')}   disabled={visibleIndex===0}              title="Flytt opp">▲</button>
          <button style={s.ctrlBtn} onClick={()=>onMove(id,'down')} disabled={visibleIndex===totalVisible-1} title="Flytt ned">▼</button>
          <span style={s.ctrlDivider} />
          <button style={{...s.ctrlBtn, color:'var(--txl)'}} onClick={()=>onHide(id)} title="Skjul widget">✕</button>
        </div>
      </div>
      {children}
    </div>
  );
}

// ─── Hoved-komponent ─────────────────────────────────────────
export default function HomePage() {
  const { user, family, members } = useAuth();
  const navigate  = useNavigate();
  const isMobile  = useIsMobile();
  const [editMode, setEditMode] = useState(false);
  const [layout, setLayout]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [activeId, setActiveId] = useState(null);

  const today    = new Date().toISOString().slice(0,10);
  const in30days = new Date(Date.now()+30*86400000).toISOString();

  const { data:todos }    = useApi('/api/todos');
  const { data:events }   = useApi(`/api/events?from=${today}T00:00:00Z&to=${in30days}`);
  const { data:messages } = useApi('/api/messages?limit=4');
  const { data:weather, loading:wLoad } = useApi('/api/weather');
  const { data:googleStatus } = useApi('/api/google/status');
  const { data:newsItems, loading:newsLoad, refresh:refreshNews } = useApi('/api/rss/combined', []);
  const { data:homeworkData } = useApi('/api/homework');

  // Modal-state for hurtigvisning
  const [selectedTodo,   setSelectedTodo]   = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedEvent,  setSelectedEvent]  = useState(null);

  // Last og migrer layout
  useEffect(()=>{
    api.get('/api/auth/layout').then(data=>{
      if(data && Array.isArray(data)){
        const migrated = data.map(w=>{
          if(!w.size){ const def=ALL_WIDGETS.find(x=>x.id===w.id); return {...w,size:def?.defaultSize||'full'}; }
          return w;
        });
        for(const widget of ALL_WIDGETS){
          if(!migrated.find(m=>m.id===widget.id))
            migrated.push({ id:widget.id, visible:true, size:widget.defaultSize });
        }
        setLayout(migrated);
      } else setLayout(DEFAULT_LAYOUT);
    }).catch(()=>setLayout(DEFAULT_LAYOUT));
  },[]);

  // Push
  useEffect(()=>{
    if(!('serviceWorker' in navigator)||!('PushManager' in window))return;
    navigator.serviceWorker.register('/sw.js').then(async reg=>{
      const v=await api.get('/api/push/vapid-public-key'); if(!v?.key)return;
      if(Notification.permission==='default') await Notification.requestPermission();
      if(Notification.permission!=='granted')return;
      const sub=await reg.pushManager.getSubscription()||await reg.pushManager.subscribe({userVisibleOnly:true,applicationServerKey:urlB64(v.key)});
      await api.post('/api/push/subscribe',{subscription:sub});
    }).catch(()=>{});
  },[]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint:{ distance:8 } }),
    useSensor(TouchSensor,   { activationConstraint:{ delay:180, tolerance:8 } })
  );

  // Brukes i BEGGE moduser: lagre umiddelbart i normal, kun til state i editMode
  const onDragEnd = useCallback(async ({ active, over })=>{
    setActiveId(null);
    if(!over || active.id===over.id) return;
    const newLayout = arrayMove(layout, layout.findIndex(x=>x.id===active.id), layout.findIndex(x=>x.id===over.id));
    setLayout(newLayout);
    if(!editMode) await api.put('/api/auth/layout', newLayout).catch(()=>{});
  },[layout, editMode]);

  const toggleWidget = useCallback(id=>{
    setLayout(l=>l.map(w=>w.id===id?{...w,visible:!w.visible}:w));
  },[]);

  const setSize = useCallback((id, size)=>{
    setLayout(l=>l.map(w=>w.id===id?{...w,size}:w));
  },[]);

  // Skjul widget raskt (uten editMode)
  const hideWidget = useCallback(async (id)=>{
    const nl = layout.map(w=>w.id===id?{...w,visible:false}:w);
    setLayout(nl);
    await api.put('/api/auth/layout', nl).catch(()=>{});
  },[layout]);

  // Resize + lagre umiddelbart (normalvisning)
  const quickResize = useCallback(async (id, newSize)=>{
    const nl = layout.map(w=>w.id===id?{...w,size:newSize}:w);
    setLayout(nl);
    await api.put('/api/auth/layout', nl).catch(()=>{});
  },[layout]);

  // Flytt opp/ned + lagre umiddelbart (normalvisning)
  const moveWidget = useCallback(async (id, dir)=>{
    const visible = layout.filter(w=>w.visible);
    const vi = visible.findIndex(x=>x.id===id);
    if(dir==='up'   && vi===0) return;
    if(dir==='down' && vi===visible.length-1) return;
    const targetId = visible[dir==='up' ? vi-1 : vi+1].id;
    const ai = layout.findIndex(x=>x.id===id);
    const bi = layout.findIndex(x=>x.id===targetId);
    const nl = arrayMove(layout, ai, bi);
    setLayout(nl);
    await api.put('/api/auth/layout', nl).catch(()=>{});
  },[layout]);

  const saveLayout = async()=>{
    setSaving(true);
    await api.put('/api/auth/layout', layout).catch(()=>{});
    setSaving(false);
    setEditMode(false);
  };

  const cancelEdit = async()=>{
    const data = await api.get('/api/auth/layout').catch(()=>null);
    setLayout(data && Array.isArray(data) ? data : DEFAULT_LAYOUT);
    setEditMode(false);
  };

  if(isMobile) return <MobileHome />;
  if(!layout) return <div style={s.loading}>Laster…</div>;

  const openTodos   = todos?.filter(t=>!t.done)||[];
  const highPri     = openTodos.filter(t=>t.priority==='high');
  const shownTodos  = (highPri.length?highPri:openTodos).slice(0,4);
  const upcomingEvs = events?.slice(0,5)||[];
  const hour        = new Date().getHours();
  const greeting    = hour<5?'God natt':hour<12?'God morgen':hour<17?'God ettermiddag':'God kveld';

  const renderWidget = (id, size='full') => {
    switch(id) {
      case 'quicknav': return (
        <div style={s.card}>
          <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
            {QUICK_NAV.map(n=>(
              <button key={n.path} style={{...s.quickBtn, flex: size==='half'?'1 1 calc(50% - 4px)':'0 1 auto'}} onClick={()=>navigate(n.path)}>
                <span style={{...s.quickIcon,background:n.color+'22',color:n.color}}>{n.icon}</span>
                <span style={s.quickLabel}>{n.label}</span>
              </button>
            ))}
          </div>
        </div>
      );

      case 'stats': {
        const todayEvs = (events||[]).filter(e=>e.start_time?.startsWith(today));
        const statsItems = [
          { label:'Hendelser i dag', val:todayEvs.length, color:'#7C6FF7', icon:'📅',
            onClick: ()=>todayEvs.length && setSelectedEvent({ ...todayEvs[0], _isStatClick: true, _all: todayEvs }) },
          { label:'Åpne oppgaver', val:openTodos.length, color:'#F7605C', icon:'✅',
            onClick: ()=>openTodos.length && setSelectedTodo({ _isStatClick:true, _all:openTodos, title:'Åpne oppgaver', description:`${openTodos.length} oppgaver venter` }) },
          { label:'Familiemedlemmer', val:members.length, color:'#34D399', icon:'👨‍👩‍👧',
            onClick: ()=>members.length && setSelectedMember(members[0]) },
          { label:'Nye meldinger', val:(messages||[]).length, color:'#FBBF24', icon:'💬',
            onClick: ()=>navigate('/chat') },
        ];
        return (
          <div style={{display:'grid', gridTemplateColumns: size==='half'?'repeat(2,1fr)':'repeat(4,1fr)', gap:10}}>
            {statsItems.map(st=>(
              <div key={st.label} style={{...s.statCard, cursor: st.val>0 ? 'pointer' : 'default'}} onClick={st.onClick}>
                <div style={s.statTop}><span>{st.icon}</span><span style={{...s.statNum,color:st.color}}>{st.val}</span></div>
                <div style={s.statLbl}>{st.label}</div>
              </div>
            ))}
          </div>
        );
      }

      case 'weather': return !wLoad&&weather?(
        <div style={s.card}>
          <div style={{display:'flex', flexDirection: size==='half'?'column':'row', gap: size==='half'?10:16, alignItems: size==='half'?'flex-start':'center'}}>
            <div style={s.wxNow}>
              <span style={{fontSize: size==='half'?40:52, lineHeight:1}}>{wxEmoji(weather.now?.symbol)}</span>
              <div>
                <div style={{fontSize: size==='half'?28:36, fontWeight:800, color:'var(--txt)', lineHeight:1}}>{fmt(weather.now?.temp)}</div>
                <div style={s.wxLoc}>📍 {weather.location}</div>
                <div style={s.wxMeta}>{weather.now?.wind!=null&&`💨${Math.round(weather.now.wind)}m/s  `}{weather.now?.humid!=null&&`💧${Math.round(weather.now.humid)}%`}</div>
              </div>
            </div>
            <div style={{display:'flex', gap:5, flex:1, overflowX:'auto', justifyContent: size==='half'?'space-between':'flex-start'}}>
              {(size==='half' ? weather.forecast?.slice(0,4) : weather.forecast)?.map(d=>(
                <div key={d.date} style={{...s.fcDay, flex: size==='half'?1:'0 0 58px'}}>
                  <div style={s.fcLbl}>{dayLbl(d.date)}</div>
                  <div style={s.fcEm}>{wxEmoji(d.symbol)}</div>
                  <div style={s.fcHi}>{fmt(d.tempMax)}</div>
                  <div style={s.fcLo}>{fmt(d.tempMin)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ):null;

      case 'upcoming': return (
        <div style={s.section}>
          <div style={s.secHead}><span style={s.secTitle}>📅 Kommende hendelser</span><button style={s.linkBtn} onClick={()=>navigate('/kalender')}>Se alle →</button></div>
          {upcomingEvs.length===0?<p style={s.empty}>Ingen kommende hendelser</p>:upcomingEvs.map(ev=>(
            <div key={ev.id} style={{...s.evRow,borderLeft:`3px solid ${ev.color||'#7C6FF7'}`,cursor:'pointer'}} onClick={()=>setSelectedEvent(ev)}>
              <div style={s.evTitle}>{ev.title}</div>
              <div style={s.evMeta}>{timeStr(ev.start_time)}{ev.location&&` · 📍${ev.location}`}</div>
            </div>
          ))}
        </div>
      );

      case 'todos': return (
        <div style={s.section}>
          <div style={s.secHead}><span style={s.secTitle}>✅ Oppgaver{highPri.length>0&&' 🔴'}</span><button style={s.linkBtn} onClick={()=>navigate('/todo')}>Se alle →</button></div>
          {shownTodos.length===0?<p style={s.empty}>Ingen åpne oppgaver 🎉</p>:shownTodos.map(t=>(
            <div key={t.id} style={{...s.todoRow, cursor:'pointer'}} onClick={()=>setSelectedTodo(t)}>
              <div style={{...s.todoDot,background:t.priority==='high'?'#F7605C':t.priority==='low'?'var(--txl)':'#7C6FF7'}}/>
              <div style={s.todoText}>
                <div style={s.todoName}>{t.title}</div>
                {t.due_date&&<div style={s.todoMeta}>Frist: {new Date(t.due_date).toLocaleDateString('nb-NO',{day:'numeric',month:'short'})}</div>}
              </div>
              {t.assigned_avatar&&<UserAvatar photo={t.assigned_photo} emoji={t.assigned_avatar} color={t.assigned_color} size={26} />}
            </div>
          ))}
          {openTodos.length>4&&<p style={s.moreHint}>+{openTodos.length-4} oppgaver til</p>}
        </div>
      );

      case 'messages': return (
        <div style={s.section}>
          <div style={s.secHead}><span style={s.secTitle}>💬 Siste meldinger</span><button style={s.linkBtn} onClick={()=>navigate('/chat')}>Åpne →</button></div>
          {!(messages?.length)?<p style={s.empty}>Ingen meldinger</p>:[...(messages||[])].reverse().map(m=>(
            <div key={m.id} style={{...s.msgRow, cursor:'pointer', borderRadius:8, transition:'background .12s'}}
              onClick={()=>navigate('/chat')}
              onMouseOver={e=>e.currentTarget.style.background='var(--bg2)'}
              onMouseOut={e=>e.currentTarget.style.background='transparent'}
            >
              <UserAvatar photo={m.user_photo} emoji={m.avatar} name={m.user_name} color={m.color} size={28} />
              <div style={s.msgBody}>
                <div style={s.msgMeta}><strong>{m.user_name}</strong> · {timeStr(m.created_at)}</div>
                <div style={s.msgTxt}>{m.content}</div>
              </div>
            </div>
          ))}
        </div>
      );

      case 'members': return (
        <div style={s.section}>
          <div style={s.secHead}>
            <span style={s.secTitle}>👨‍👩‍👧 Familien</span>
            {user?.role === 'admin' && (
              <button style={s.linkBtn} onClick={() => navigate('/innstillinger')}>+ Legg til</button>
            )}
          </div>
          <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
            {members.map(m=>{
              const age = calcAge(m.birth_date);
              const bday = m.birth_date
                ? new Date(m.birth_date).toLocaleDateString('nb-NO',{day:'numeric',month:'short',year:'numeric'})
                : null;
              return (
                <div key={m.id} style={{...s.memCard, cursor:'pointer'}} onClick={()=>setSelectedMember(m)}>
                  <UserAvatar photo={m.photo} emoji={m.avatar} name={m.name} color={m.color} size={36} />
                  <div style={s.memName}>{m.name.split(' ')[0]}</div>
                  {bday && <div style={{fontSize:10, color:'var(--txl)', textAlign:'center', lineHeight:1.4}}>{bday}</div>}
                  {age !== null && <div style={{fontSize:10, color:'var(--accent)', fontWeight:700}}>{age} år</div>}
                  <div style={s.memRole}>{m.role==='admin'?'👑':m.account_type==='child'?'🧒':'👤'}</div>
                </div>
              );
            })}
          </div>
        </div>
      );

      case 'news': return (
        <div style={s.section}>
          <div style={s.secHead}>
            <span style={s.secTitle}>📰 Nyheter</span>
            <button style={s.linkBtn} onClick={refreshNews}>↻ Oppdater</button>
          </div>
          {newsLoad && <p style={s.empty}>Henter nyheter…</p>}
          {!newsLoad && (!newsItems||newsItems.length===0) && (
            <p style={s.empty}>Ingen nyhetskilder valgt — gå til <strong>Innstillinger → Nyhetskilder</strong>.</p>
          )}
          {!newsLoad && newsItems?.slice(0, size==='half'?8:14).map((item,i)=>(
            <a key={i} href={item.link} target="_blank" rel="noopener noreferrer" style={s.newsRow}>
              <div style={s.newsSource}>{item.source}</div>
              <div style={s.newsTitle}>{item.title}</div>
              <div style={s.newsTime}>{newsAgo(item.pubDate)}</div>
            </a>
          ))}
        </div>
      );

      case 'homework': {
        const pendingHW = (homeworkData||[]).filter(h=>!h.done && h.source==='itslearning');
        if (!pendingHW.length) return null;
        const byMember = {};
        pendingHW.forEach(h => {
          const key = h.user_id;
          if (!byMember[key]) byMember[key] = { name: h.user_name || members.find(m=>m.id===h.user_id)?.name || 'Ukjent', items: [] };
          byMember[key].items.push(h);
        });
        return (
          <div style={s.section}>
            <div style={s.secHead}><span style={s.secTitle}>📚 Lekser</span><button style={s.linkBtn} onClick={()=>navigate('/lekser')}>Se alle →</button></div>
            {Object.values(byMember).map(({ name, items }) => (
              <div key={name} style={{marginBottom:6}}>
                <div style={{fontSize:11, fontWeight:700, color:'var(--txm)', marginBottom:3}}>{name}</div>
                {items.slice(0,3).map(h=>(
                  <div key={h.id} style={{...s.evRow, borderLeft:'3px solid #7C6FF7', cursor:'pointer', marginBottom:3}} onClick={()=>setSelectedTodo({...h, title: h.title, description: h.subject, due_date: h.due_date, isHomework:true})}>
                    <div style={s.evTitle}>{h.title}</div>
                    <div style={s.evMeta}>{h.subject}{h.due_date&&` · Frist: ${new Date(h.due_date).toLocaleDateString('nb-NO',{day:'numeric',month:'short'})}`}</div>
                  </div>
                ))}
                {items.length>3&&<div style={s.moreHint}>+{items.length-3} til</div>}
              </div>
            ))}
          </div>
        );
      }

      case 'gcal': return googleStatus?.configured?(
        <div style={s.section}>
          {googleStatus.connected?(
            <button style={s.gcBtn} onClick={()=>api.post('/api/google/calendar/sync',{}).then(r=>r.synced>0&&alert(`✅ ${r.synced} hendelser synkronisert`)).catch(()=>{})}>
              🗓 Synkroniser Google Kalender
            </button>
          ):(
            <a href="/api/google/auth?action=connect" style={s.gcBtn}>🔗 Koble til Google Kalender</a>
          )}
        </div>
      ):null;

      default: return null;
    }
  };

  // ─── Render ────────────────────────────────────────────────
  const visibleLayout = layout.filter(w=>w.visible && w.id !== 'weather');

  return (
    <div style={s.wrap}>
      {/* Toprad – fast, ikke tilpassbar */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, minHeight:48 }}>
        <div>
          <h2 style={s.greeting}>{greeting}, {user?.name?.split(' ')[0]}! 👋</h2>
          <p style={s.sub}>{family?.name} · {new Date().toLocaleDateString('nb-NO',{weekday:'long',day:'numeric',month:'long'})}</p>
        </div>
        {!wLoad && weather && (
          <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, color:'var(--txm)', whiteSpace:'nowrap', flexShrink:0 }}>
            <span style={{fontSize:20}}>{wxEmoji(weather.now?.symbol)}</span>
            <span style={{fontSize:18, fontWeight:800, color:'var(--txt)'}}>{fmt(weather.now?.temp)}</span>
            {weather.location && <><span style={s.wxDot}>·</span><span>{weather.location}</span></>}
            {weather.now?.wind != null && <><span style={s.wxDot}>·</span><span>💨 {Math.round(weather.now.wind)}m/s</span></>}
            {weather.forecast?.[0] && <><span style={s.wxDot}>·</span><span style={{color:'#FF6B35', fontWeight:700}}>I dag {fmt(weather.forecast[0].tempMax)}</span></>}
            {weather.forecast?.[1] && <><span style={s.wxDot}>·</span><span>{dayLbl(weather.forecast[1].date)} {fmt(weather.forecast[1].tempMax)}</span></>}
            {weather.forecast?.[2] && <><span style={s.wxDot}>·</span><span>{dayLbl(weather.forecast[2].date)} {fmt(weather.forecast[2].tempMax)}</span></>}
          </div>
        )}
      </div>

      {/* Widget-kontroller */}
      <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'center', gap:8, marginBottom: editMode ? 4 : 0 }}>
        {editMode ? (
          <>
            <button style={s.saveBtn} onClick={saveLayout} disabled={saving}>{saving?'Lagrer…':'💾 Lagre'}</button>
            <button style={s.cancelBtn} onClick={cancelEdit}>Avbryt</button>
          </>
        ) : (
          <button style={s.editBtn} onClick={()=>setEditMode(true)}>⚙️ Widgeter</button>
        )}
      </div>

      {editMode && (
        <div style={s.editHint}>
          ⠿ Dra for å endre rekkefølge · <strong>◼ Full / ◧ Halv</strong> for bredde · <strong>Synlig/Skjult</strong> for å vise/skjule · <strong>✕</strong> i widget-hjørnet for rask skjuling
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={({active})=>setActiveId(active.id)}
        onDragEnd={onDragEnd}
      >
        {/* ── Redigeringsmodus: enkel 1-kolonne liste ── */}
        {editMode && (
          <SortableContext items={layout.map(w=>w.id)} strategy={verticalListSortingStrategy}>
            <div style={s.widgetList}>
              {layout.filter(w => w.id !== 'weather').map(({id,visible,size})=>{
                const meta = ALL_WIDGETS.find(w=>w.id===id);
                if(!meta) return null;
                const content = renderWidget(id, size||'full');
                return (
                  <SortableWidget key={id} id={id} editMode={true}
                    visible={visible} size={size||'full'}
                    onToggle={toggleWidget} onSize={setSize}
                    label={meta.label} icon={meta.icon}>
                    {content||<div style={s.placeholder}>Ikke tilgjengelig</div>}
                  </SortableWidget>
                );
              })}
            </div>
          </SortableContext>
        )}

        {/* ── Normalvisning: smart rad-pairing ── */}
        {!editMode && (
          <SortableContext items={visibleLayout.map(w=>w.id)} strategy={rectSortingStrategy}>
            <div style={s.rowStack}>
              {buildRows(visibleLayout).map((row, ri) => (
                <div key={ri} style={s.widgetRow}>
                  {row.map(({ id, size }, wi) => {
                    const sz      = size||'full';
                    const meta    = ALL_WIDGETS.find(w=>w.id===id);
                    const content = renderWidget(id, sz);
                    if(!content) return null;
                    const vi = visibleLayout.findIndex(x=>x.id===id);
                    return (
                      <GridWidget
                        key={id} id={id}
                        flex={sz==='half' ? '1 1 0' : '1 1 100%'}
                        meta={meta} size={sz}
                        isMobile={isMobile}
                        onResize={quickResize}
                        onMove={moveWidget}
                        onHide={hideWidget}
                        visibleIndex={vi}
                        totalVisible={visibleLayout.length}
                      >
                        {content}
                      </GridWidget>
                    );
                  })}
                </div>
              ))}
            </div>
          </SortableContext>
        )}

        <DragOverlay>
          {activeId&&(
            <div style={s.dragOverlay}>
              {ALL_WIDGETS.find(w=>w.id===activeId)?.icon}{' '}
              {ALL_WIDGETS.find(w=>w.id===activeId)?.label}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* ── Todo/Lekse-modal ─────────────────────────────────── */}
      {selectedTodo && (
        <div style={s.modalOverlay} onClick={()=>setSelectedTodo(null)}>
          <div style={s.modalBox} onClick={e=>e.stopPropagation()}>
            <div style={s.modalHead}>
              <div style={{flex:1}}>
                <div style={{fontSize:10,fontWeight:700,color:'var(--txl)',textTransform:'uppercase',marginBottom:4}}>
                  {selectedTodo.isHomework?'📚 Lekse':'✅ Oppgave'}
                  {selectedTodo.priority==='high'&&<span style={{marginLeft:6,color:'#F7605C'}}>🔴 Høy prioritet</span>}
                </div>
                <div style={{fontSize:17,fontWeight:800,color:'var(--txt)'}}>{selectedTodo.title}</div>
              </div>
              <button onClick={()=>setSelectedTodo(null)} style={s.modalClose}>✕</button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:12}}>
              {selectedTodo.description&&<div style={s.modalInfo}><span style={s.modalLbl}>Beskrivelse</span><span>{selectedTodo.description}</span></div>}
              {selectedTodo.subject&&!selectedTodo.isHomework&&<div style={s.modalInfo}><span style={s.modalLbl}>Fag</span><span>{selectedTodo.subject}</span></div>}
              {selectedTodo.due_date&&<div style={s.modalInfo}><span style={s.modalLbl}>Frist</span><span style={{color:new Date(selectedTodo.due_date)<new Date()?'var(--red)':'var(--txt)'}}>{new Date(selectedTodo.due_date).toLocaleDateString('nb-NO',{weekday:'long',day:'numeric',month:'long'})}</span></div>}
              {selectedTodo.assigned_name&&<div style={s.modalInfo}><span style={s.modalLbl}>Tildelt</span><span>{selectedTodo.assigned_name}</span></div>}
            </div>
            {!selectedTodo.isHomework&&(
              <button style={{...s.modalBtn,marginTop:14}} onClick={()=>{setSelectedTodo(null);navigate('/todo');}}>Åpne oppgaver →</button>
            )}
          </div>
        </div>
      )}

      {/* ── Familiemedlem-modal ───────────────────────────────── */}
      {selectedMember && (
        <div style={s.modalOverlay} onClick={()=>setSelectedMember(null)}>
          <div style={s.modalBox} onClick={e=>e.stopPropagation()}>
            <div style={s.modalHead}>
              <UserAvatar photo={selectedMember.photo} emoji={selectedMember.avatar} name={selectedMember.name} color={selectedMember.color} size={48} />
              <div style={{flex:1,marginLeft:12}}>
                <div style={{fontSize:17,fontWeight:800,color:'var(--txt)'}}>{selectedMember.name}</div>
                <div style={{fontSize:12,color:'var(--txl)',marginTop:2}}>
                  {selectedMember.role==='admin'?'👑 Administrator':selectedMember.account_type==='child'?'🧒 Barn':'👤 Familiemedlem'}
                </div>
              </div>
              <button onClick={()=>setSelectedMember(null)} style={s.modalClose}>✕</button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:12}}>
              {selectedMember.birth_date&&(
                <div style={s.modalInfo}>
                  <span style={s.modalLbl}>Fødselsdato</span>
                  <span>
                    {new Date(selectedMember.birth_date).toLocaleDateString('nb-NO',{day:'numeric',month:'long',year:'numeric'})}
                    {calcAge(selectedMember.birth_date)!==null&&<span style={{marginLeft:8,background:'var(--bg2)',borderRadius:20,padding:'1px 8px',fontSize:11,fontWeight:700}}>{calcAge(selectedMember.birth_date)} år</span>}
                  </span>
                </div>
              )}
              {selectedMember.email&&<div style={s.modalInfo}><span style={s.modalLbl}>E-post</span><span style={{color:'var(--accent)'}}>{selectedMember.email}</span></div>}
              {selectedMember.last_seen&&<div style={s.modalInfo}><span style={s.modalLbl}>Sist aktiv</span><span>{timeStr(selectedMember.last_seen)}</span></div>}
            </div>
          </div>
        </div>
      )}

      {/* ── Hendelse-modal ────────────────────────────────────── */}
      {selectedEvent && (
        <div style={s.modalOverlay} onClick={()=>setSelectedEvent(null)}>
          <div style={s.modalBox} onClick={e=>e.stopPropagation()}>
            <div style={s.modalHead}>
              <div style={{width:4,alignSelf:'stretch',borderRadius:4,background:selectedEvent.color||'#7C6FF7',marginRight:12,flexShrink:0}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:17,fontWeight:800,color:'var(--txt)'}}>{selectedEvent.title}</div>
                <div style={{fontSize:12,color:'var(--txl)',marginTop:2}}>{timeStr(selectedEvent.start_time)}</div>
              </div>
              <button onClick={()=>setSelectedEvent(null)} style={s.modalClose}>✕</button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:12}}>
              {selectedEvent.location&&<div style={s.modalInfo}><span style={s.modalLbl}>📍 Sted</span><span>{selectedEvent.location}</span></div>}
              {selectedEvent.description&&<div style={s.modalInfo}><span style={s.modalLbl}>Beskrivelse</span><span>{selectedEvent.description}</span></div>}
              {selectedEvent.end_time&&selectedEvent.end_time!==selectedEvent.start_time&&<div style={s.modalInfo}><span style={s.modalLbl}>Slutter</span><span>{timeStr(selectedEvent.end_time)}</span></div>}
              {selectedEvent.user_name&&<div style={s.modalInfo}><span style={s.modalLbl}>Lagt til av</span><span>{selectedEvent.user_name}</span></div>}
            </div>
            <button style={{...s.modalBtn,marginTop:14}} onClick={()=>{setSelectedEvent(null);navigate('/kalender');}}>Åpne kalender →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Edit-mode widget styles ──────────────────────────────────
const sw = {
  editBar:      { display:'flex', alignItems:'center', gap:8, padding:'7px 12px', background:'var(--bg)', borderRadius:'12px 12px 0 0', border:'1px solid var(--bdr)', borderBottom:'none' },
  handle:       { fontSize:18, color:'var(--txl)', cursor:'grab', userSelect:'none', letterSpacing:1, lineHeight:1, flexShrink:0 },
  editLabel:    { fontSize:12, fontWeight:700, color:'var(--txm)', flex:1 },
  editBtns:     { display:'flex', gap:5, alignItems:'center', flexWrap:'wrap' },
  sizeBtn:      { padding:'3px 9px', borderRadius:7, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', cursor:'pointer', fontSize:11, fontWeight:700, fontFamily:'inherit' },
  sizeBtnActive:{ background:'#7C6FF7', borderColor:'#7C6FF7', color:'#fff' },
  toggleBtn:    { padding:'3px 10px', borderRadius:20, border:'none', fontWeight:700, cursor:'pointer', fontSize:11, fontFamily:'inherit' },
  toggleOn:     { background:'var(--green-bg)', color:'var(--green)' },
  toggleOff:    { background:'var(--red-bg)', color:'var(--red)' },
  widgetWrap:   {},
  hiddenWidget: { opacity:.4, pointerEvents:'none' },
};

// ─── Hoved-styles ─────────────────────────────────────────────
const s = {
  wrap:       { display:'flex', flexDirection:'column', gap:12 },
  loading:    { padding:40, textAlign:'center', color:'var(--txl)' },
  topRow:     { display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16 },
  greeting:   { fontSize:22, fontWeight:800, color:'var(--txt)', margin:0 },
  sub:        { fontSize:13, color:'var(--txl)', margin:'4px 0 0' },
  topActions: { display:'flex', gap:8, alignItems:'center', flexShrink:0 },
  editBtn:    { padding:'8px 16px', borderRadius:10, border:'1px solid var(--bdr)', background:'var(--card)', color:'#7C6FF7', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  saveBtn:    { padding:'8px 16px', borderRadius:10, border:'none', background:'#7C6FF7', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  cancelBtn:  { padding:'8px 14px', borderRadius:10, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  editHint:   { background:'var(--bg)', border:'1px solid var(--bdr)', borderRadius:10, padding:'8px 14px', fontSize:12, color:'var(--txm)', lineHeight:1.6 },
  widgetList: { display:'flex', flexDirection:'column', gap:10 },
  rowStack:   { display:'flex', flexDirection:'column', gap:12 },
  widgetRow:  { display:'flex', gap:12, alignItems:'stretch' },
  dragOverlay:{ background:'#7C6FF7', color:'#fff', padding:'10px 18px', borderRadius:12, fontWeight:700, fontSize:14, boxShadow:'0 8px 24px rgba(124,111,247,.4)' },
  placeholder:{ padding:20, textAlign:'center', color:'var(--txl)', fontSize:13 },

  // Kontrollstripe (drag-håndtak + størrelse + rekkefølge)
  ctrlBar:    { display:'flex', alignItems:'center', gap:6, padding:'4px 8px', marginBottom:3, borderRadius:8, background:'var(--bg2)', border:'1px solid var(--bdr)', cursor:'grab', userSelect:'none' },
  ctrlGrip:   { fontSize:14, color:'var(--txl)', letterSpacing:1, flexShrink:0 },
  ctrlLabel:  { fontSize:11, fontWeight:700, color:'var(--txl)', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  ctrlRight:  { display:'flex', gap:3, alignItems:'center', flexShrink:0 },
  ctrlBtn:    { width:24, height:22, padding:0, borderRadius:6, border:'1px solid var(--bdr)', background:'var(--card)', color:'#7C6FF7', cursor:'pointer', fontSize:10, fontWeight:800, fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', disabled:{ opacity:0.3 } },
  ctrlBtnOn:  { background:'#7C6FF7', color:'#fff', borderColor:'#7C6FF7' },
  ctrlDivider:{ width:1, height:14, background:'var(--bdr)', margin:'0 2px', flexShrink:0 },

  // Widgets
  card:       { background:'var(--card)', borderRadius:16, padding:'14px 16px', border:'1px solid var(--bdr)' },
  section:    { background:'var(--card)', borderRadius:16, padding:'14px 16px', border:'1px solid var(--bdr)', display:'flex', flexDirection:'column', gap:8 },
  secHead:    { display:'flex', justifyContent:'space-between', alignItems:'center' },
  secTitle:   { fontSize:14, fontWeight:800, color:'var(--txt)' },
  linkBtn:    { background:'none', border:'none', color:'#7C6FF7', fontWeight:700, fontSize:12, cursor:'pointer', fontFamily:'inherit', padding:0 },

  quickBtn:   { display:'flex', flexDirection:'column', alignItems:'center', gap:5, padding:'10px 12px', borderRadius:14, border:'1px solid var(--bdr)', background:'var(--bg)', cursor:'pointer', fontFamily:'inherit', minWidth:0 },
  quickIcon:  { width:34, height:34, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 },
  quickLabel: { fontSize:11, fontWeight:700, color:'var(--txm)' },

  statCard:   { background:'var(--card)', borderRadius:14, padding:'12px 14px', border:'1px solid var(--bdr)' },
  statTop:    { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 },
  statNum:    { fontSize:22, fontWeight:800, lineHeight:1 },
  statLbl:    { fontSize:11, fontWeight:600, color:'var(--txl)' },

  wxNow:      { display:'flex', alignItems:'center', gap:12, flexShrink:0 },
  wxLoc:      { fontSize:12, color:'var(--txm)', marginTop:4 },
  wxMeta:     { fontSize:11, color:'var(--txl)', marginTop:2 },
  fcDay:      { background:'var(--bg)', borderRadius:12, padding:'7px 4px', textAlign:'center' },
  fcLbl:      { fontSize:10, fontWeight:700, color:'var(--txm)', marginBottom:2 },
  fcEm:       { fontSize:16, marginBottom:2 },
  fcHi:       { fontSize:12, fontWeight:800, color:'#F7605C' },
  fcLo:       { fontSize:11, color:'#60A5FA', marginTop:1 },

  evRow:      { padding:'7px 10px', borderRadius:9, background:'var(--bg)', marginBottom:3 },
  evTitle:    { fontSize:13, fontWeight:700, color:'var(--txt)' },
  evMeta:     { fontSize:11, color:'var(--txm)', marginTop:1 },

  todoRow:    { display:'flex', alignItems:'center', gap:10, padding:'6px 0', borderBottom:'1px solid var(--bdr)' },
  todoDot:    { width:8, height:8, borderRadius:'50%', flexShrink:0 },
  todoText:   { flex:1, minWidth:0 },
  todoName:   { fontSize:13, fontWeight:600, color:'var(--txt)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  todoMeta:   { fontSize:11, color:'var(--txl)', marginTop:1 },
  todoAv:     { width:26, height:26, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:12, flexShrink:0 },
  moreHint:   { fontSize:11, color:'var(--txl)', margin:0, textAlign:'center' },

  msgRow:     { display:'flex', gap:8, alignItems:'flex-start', padding:'5px 0', borderBottom:'1px solid var(--bdr)' },
  msgAv:      { width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:11, color:'#fff', flexShrink:0 },
  msgBody:    { flex:1, minWidth:0 },
  msgMeta:    { fontSize:11, color:'var(--txl)', marginBottom:1 },
  msgTxt:     { fontSize:13, color:'var(--txt)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },

  memCard:    { display:'flex', flexDirection:'column', alignItems:'center', gap:3, padding:'8px 12px', background:'var(--bg)', borderRadius:10 },
  memAv:      { width:36, height:36, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:15, color:'#fff' },
  memName:    { fontSize:12, fontWeight:700, color:'var(--txt)' },
  memRole:    { fontSize:13 },

  newsRow:    { display:'grid', gridTemplateColumns:'auto 1fr auto', alignItems:'baseline', gap:'0 8px', padding:'6px 0', borderBottom:'1px solid var(--bdr)', textDecoration:'none', color:'inherit' },
  newsSource: { fontSize:10, fontWeight:700, color:'#fff', background:'#7C6FF7', padding:'2px 6px', borderRadius:5, whiteSpace:'nowrap' },
  newsTitle:  { fontSize:13, fontWeight:600, color:'var(--txt)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  newsTime:   { fontSize:10, color:'var(--txl)', whiteSpace:'nowrap' },

  gcBtn:      { display:'inline-block', padding:'10px 18px', borderRadius:12, border:'none', background:'#4285F4', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', textDecoration:'none', fontFamily:"'Plus Jakarta Sans',sans-serif" },
  empty:      { color:'var(--txl)', fontSize:13, margin:0, padding:'4px 0' },

  modalOverlay: { position:'fixed', inset:0, background:'rgba(0,0,0,.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20, backdropFilter:'blur(4px)' },
  modalBox:     { background:'var(--card)', borderRadius:18, padding:22, width:'100%', maxWidth:420, boxShadow:'0 16px 48px rgba(0,0,0,.3)' },
  modalHead:    { display:'flex', alignItems:'flex-start', gap:8 },
  modalClose:   { background:'var(--bg2)', border:'none', borderRadius:'50%', width:28, height:28, cursor:'pointer', fontSize:13, color:'var(--txm)', flexShrink:0 },
  modalInfo:    { display:'flex', flexDirection:'column', gap:2, background:'var(--bg2)', borderRadius:10, padding:'8px 12px' },
  modalLbl:     { fontSize:10, fontWeight:700, color:'var(--txl)', textTransform:'uppercase', letterSpacing:.5 },
  modalBtn:     { width:'100%', padding:'10px', borderRadius:10, border:'none', background:'var(--accent)', color:'white', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' },

  wxDot:      { color:'var(--bdr)', fontWeight:400, margin:'0 2px' },

  // (ubrukte stiler fra forrige versjon, beholdes for bakoverkompatibilitet)
  wxCompact:  { background:'var(--card)', borderRadius:12, padding:'10px 12px', border:'1px solid var(--bdr)', maxWidth:340, flexShrink:0 },
  wxCTop:     { display:'flex', alignItems:'center', gap:8, marginBottom:6 },
  wxCEmoji:   { fontSize:28, lineHeight:1, flexShrink:0 },
  wxCTemp:    { fontSize:30, fontWeight:800, color:'var(--txt)', lineHeight:1 },
  wxCLoc:     { fontSize:11, color:'var(--txm)', marginTop:2 },
  wxCMeta:    { fontSize:11, color:'var(--txl)', marginTop:1 },
  wxCFore:    { display:'flex', gap:4, justifyContent:'space-between' },
  wxCDay:     { flex:1, background:'var(--bg)', borderRadius:8, padding:'4px 2px', textAlign:'center' },
  wxCDayLbl:  { fontSize:10, fontWeight:700, color:'var(--txm)', marginBottom:1 },
  wxCDayHi:   { fontSize:11, fontWeight:800, color:'#F7605C' },
  wxCDayLo:   { fontSize:10, color:'#60A5FA', marginTop:1 },
};
